import * as Yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum TagwallPurchaseOrderLineItemKeys {
  itemNumber = 'itemNumber',
  quantity = 'quantity',
  description = 'description',
  price = 'price',
  tag = 'tag',
  lineTotal = 'lineTotal',
}

const TagwallLineItemSchema = Yup.object().shape({
  [TagwallPurchaseOrderLineItemKeys.itemNumber]: Yup.number().nullable().optional(),
  [TagwallPurchaseOrderLineItemKeys.quantity]: Yup.string().nullable().optional(),
  [TagwallPurchaseOrderLineItemKeys.description]: Yup.string().nullable().optional(),
  [TagwallPurchaseOrderLineItemKeys.price]: Yup.string().nullable().optional(),
  [TagwallPurchaseOrderLineItemKeys.tag]: Yup.string().nullable().optional(),
  [TagwallPurchaseOrderLineItemKeys.lineTotal]: Yup.string().nullable().optional(),
})

export const tagwallPurchaseOrderFileDataSchema = Yup.object().shape({
  organizationLogoOverride: Yup.string().nullable().optional(),
  contact: Yup.object()
    .shape({
      name: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
    })
    .required(),
  organization: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      website: Yup.string().nullable().optional(),
    })
    .required(),
  vendor: Yup.object()
    .shape({
      vendorId: Yup.string().nullable().optional(),
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      faxCode: Yup.string().nullable().optional(),
      emailAddress: Yup.string().nullable().optional(),
    })
    .required(),
  purchaseOrderNumber: Yup.string().nullable().optional(),
  proposalNumber: Yup.string().nullable().optional(),
  accountNumber: Yup.string().nullable().optional(),
  date: Yup.string().nullable().optional(),
  destination: Yup.object()
    .shape({
      company: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      addressLine2: Yup.string().nullable().optional(),
      cityStateZip: Yup.string().nullable().optional(),
      contactName: Yup.string().nullable().optional(),
      contactFullName: Yup.string().nullable().optional(),
      contactEmail: Yup.string().nullable().optional(),
      contactPhone: Yup.string().nullable().optional(),
    })
    .required(),
  items: Yup.array().of(TagwallLineItemSchema).required(),
  note1: Yup.string().nullable().optional(),
  note2: Yup.string().nullable().optional(),
  mustShipComplete: Yup.string().nullable().optional(),
})

export type TagwallPurchaseOrderFileData = Yup.InferType<typeof tagwallPurchaseOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<TagwallPurchaseOrderFileData>

export const tagwallPurchaseOrderFileMappings: RequiredFileData = {
  organizationLogoOverride: '6b526eab-bbd4-42b6-8ef1-583b81a6214b',
  contact: {
    name: '74b7cd81-31a2-454e-a622-363e4ec1a6b2',
    phoneNumber: '94314059-2365-4f8c-acf7-00370da6f9a4',
  },
  organization: {
    companyName: 'e91f5d7a-2c8b-4e01-9d3c-4a1e2f3b6c7d',
    addressLine1: 'b3f8d2e4-7a9c-4e0a-bc1d-2f3e4a5b6c7d',
    addressLine2: '1e38b40f-b495-447e-9e5f-8fc6dc0c1caf',
    cityStateZip: '1a2b3c4d-5e6f-4789-abcd-ef0123456789',
    phoneNumber: '8b9c0d1e-2f3a-4b5c-6d7e-8f9a0b1c2d3e',
    website: '2c41e2c7-4299-4e5a-a8e2-b181c3f7e446',
  },
  vendor: {
    vendorId: 'b7b8af51-0dea-4410-8ca9-9160d1f3e324',
    companyName: '1595d672-1f67-408d-942b-4082c6970e18',
    addressLine1: 'd77daa94-b317-4262-afd2-6b1a02001539',
    addressLine2: '2c2ccc80-99db-4056-980f-b87656045e36',
    cityStateZip: '53e33065-6941-433d-80af-7b10e49dbbe7',
    faxCode: 'c96961dc-1e11-4ab6-902e-e5556c47f822',
    emailAddress: '9254f3ea-33b8-4d9f-9ffe-f90ea96b9da8',
  },
  purchaseOrderNumber: 'd1abfc0e-6ec2-4911-8ce2-0d4d65fef1db',
  proposalNumber: 'bf5dfcf5-0f5c-4162-bd3d-17f66fe66300',
  accountNumber: 'e1dfb89f-7127-420f-98a6-67af85f19150',
  date: '99e0683c-cb31-41c1-9f9e-c3bca8474c51',
  destination: {
    company: '9fabd2c8-3396-4de7-b79b-39224a4f52db',
    addressLine1: '2d8e4482-79c6-4e95-9820-9a0ba0cf1803',
    addressLine2: 'f3f8c667-307c-4f6b-971a-86bc2c291ddc',
    cityStateZip: 'e272fa15-67ce-4a5b-b526-e74a81f4309f',
    contactName: 'd6857772-2b4e-4cbb-ab90-5a186a6f75d8',
    contactFullName: 'c02cee17-d6d1-446f-b09c-cea3a44cb658',
    contactEmail: 'fb4a21f0-7bf3-4d9c-a6f3-9db123d0217e',
    contactPhone: 'f0f352dc-996a-4362-bd48-7633297ef237',
  },
  note1: '398cb317-b730-4b7f-94c8-47cdb60b4ddc',
  note2: '39b9e6b9-6172-4c8c-a6fe-3b4a026c1791',
  mustShipComplete: '5b20847c-a7a6-4a20-b2b8-8aead6ea592a',
  items: [
    {
      itemNumber: null,
      quantity: '465c8e00-24cf-4e02-8617-bba774800c73',
      description: '6566f5ff-3d2e-4d85-bdc3-80f5d172631c',
      price: 'e5b7c7eb-f772-4d32-9846-2ae366515de5',
      tag: '3c852e74-c871-4ea4-aead-d00dd6f7a787',
      lineTotal: null,
    },
  ],
}
